import React from 'react';

const reportCss = `
  * { box-sizing: border-box; }
  body {
    margin: 24px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #1e293b;
    font-size: 12px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .report-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    border-bottom: 2px solid #1e293b;
    padding-bottom: 10px;
    margin-bottom: 16px;
  }
  .report-header h1 { margin: 0; font-size: 20px; letter-spacing: -0.02em; }
  .report-date { color: #64748b; font-size: 12px; }
  .card {
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 12px;
    break-inside: avoid;
  }
  .card-title { display: flex; align-items: center; gap: 8px; margin: 0 0 8px; }
  .card-title h2 { margin: 0; font-size: 14px; }
  .count-pill {
    display: inline-block;
    min-width: 20px;
    padding: 1px 8px;
    border-radius: 999px;
    background: #e2e8f0;
    color: #1e293b;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
  }
  .row { padding: 4px 0; border-bottom: 1px solid #f1f5f9; line-height: 1.5; }
  .row:last-child { border-bottom: none; }
  .veh { font-weight: 700; }
  .muted { color: #64748b; }
  .faint { color: #94a3b8; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 4px 6px; text-align: right; }
  td { white-space: nowrap; }
  thead th {
    color: #64748b;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.3;
    vertical-align: bottom;
    border-bottom: 1px solid #cbd5e1;
  }
  tbody tr { border-bottom: 1px solid #f1f5f9; }
  tbody th.type-col { font-weight: 600; }
  .type-col { text-align: left; padding-left: 0; }
  .total-col { font-weight: 700; }
  .totals-row th, .totals-row td { font-weight: 700; color: #64748b; border-top: 1px solid #cbd5e1; }
`;

function MakeReport({summaryReport, oos, atVendor, forVendor, accidents, breakdowns}) {

    const date = new Date();
    const dtString = date.toLocaleString('en-US', {timeZone: 'America/New_York'}).replace(",","").replace(/:.. /," ");

    const formatDate = dateString => {
      const dtObject = new Date(dateString);
      return dtObject.toLocaleString('en-US', {timeZone: 'America/New_York'}).replace(",","").replace(/:.. /," ")
    }

    const getTotalIssues = items => {
      return items.reduce((acc, item) => {
        return acc + item.issues.length;
      }, 0);
    };

    const groupByOrders = vehicles => [...vehicles].sort((a, b) => (b.orders.length > 0) - (a.orders.length > 0));

    // Fleet status table: rows are types (by total), columns are statuses (by fleet-wide count)
    const statusTotals = {};
    Object.values(summaryReport).forEach((counts) => {
      Object.keys(counts).forEach((status) => {
        if (status !== 'total') {
          statusTotals[status] = (statusTotals[status] || 0) + counts[status];
        }
      });
    });
    const statusColumns = Object.keys(statusTotals).sort((a, b) => statusTotals[b] - statusTotals[a]);
    const typeRows = Object.keys(summaryReport).sort((a, b) => summaryReport[b].total - summaryReport[a].total);

    const renderOrders = (item, showIssued) => {
      if (item.orders.length === 0) {
        return <span className='faint'> No WOs</span>;
      }
      return <span className='muted'> WOs [ {item.orders.map((order, index) => (
        <span key={order.order_number}>
          {order.order_number}
          <span className='faint'> ({order.items && order.items.length > 0 ? order.items.join(', ') : 'None'})</span>
          {showIssued && order.issued_by && <span className='faint'> issued by {order.issued_by} on {formatDate(order.issued_at)}</span>}
          {index < item.orders.length - 1 && ', '}
        </span>
      ))} ]</span>;
    };

    const renderStatusSection = (title, vehicles, showIssued) => (
      <div className='card'>
        <div className='card-title'>
          <h2>{title}</h2>
          <span className='count-pill'>{vehicles.length}</span>
        </div>
        {groupByOrders(vehicles).map((item) => (
          <div className='row' key={item.id}>
            <span className='veh'>{item.name}</span>
            {renderOrders(item, showIssued)}
          </div>
        ))}
      </div>
    );

    const renderIssueSection = (title, items) => (
      <div className='card'>
        <div className='card-title'>
          <h2>{title}</h2>
          <span className='count-pill'>{getTotalIssues(items)}</span>
        </div>
        {items.map((item) => (
          <div className='row' key={item.id}>
            <span className='veh'>{item.name}</span>
            <span className='muted'> {item.issues.map((issue) => `Issue ${issue.number}`).join(', ')}</span>
          </div>
        ))}
      </div>
    );

    return <div>
      <style>{reportCss}</style>
      <div className='report-header'>
        <h1>NYCSBUS Fleet's Eye Report</h1>
        <span className='report-date'>{dtString}</span>
      </div>

      <div className='card'>
        <div className='card-title'>
          <h2>Fleet Status</h2>
        </div>
        <table>
          <thead>
            <tr>
              <th className='type-col'>Asset Type</th>
              <th>Total</th>
              {statusColumns.map((status) => <th key={status}>{status}</th>)}
            </tr>
          </thead>
          <tbody>
            {typeRows.map((typeName) => {
              const counts = summaryReport[typeName];
              return <tr key={typeName}>
                <th className='type-col'>{typeName}</th>
                <td className='total-col'>{counts.total}</td>
                {statusColumns.map((status) => <td key={status} className={counts[status] ? '' : 'faint'}>{counts[status] || '\u2013'}</td>)}
              </tr>
            })}
            <tr className='totals-row'>
              <th className='type-col'>All Types</th>
              <td className='total-col'>{typeRows.reduce((sum, typeName) => sum + summaryReport[typeName].total, 0)}</td>
              {statusColumns.map((status) => <td key={status}>{statusTotals[status]}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      {renderStatusSection('OOS', oos, true)}
      {renderStatusSection('At Vendor', atVendor, false)}
      {renderStatusSection('For Vendor', forVendor, false)}
      {renderIssueSection('Accidents', accidents)}
      {renderIssueSection('Breakdowns', breakdowns)}
    </div>;
}

export default MakeReport;
